import React from "react";
import layoutStyles from "../PanelUser/PanelLayout.module.css";
import styles from "./MisRecetas.module.css";
import axiosInstance from "../../api/axiosConfig";
import { loadIzipaySdk } from "../../js/izipaySdk";
import { GetTokenSession } from "../../js/getTokenSession";
import { getDataOrderDynamic } from "../../js/util";
import {
  PanelSidebar,
  PanelHeader,
  useUserData,
  formatDate,
  MOCK_USER,
} from "../PanelUser/PanelShared";

const MERCHANT_CODE = process.env.REACT_APP_IZIPAY_MERCHANT_CODE || "4004353";
const PUBLIC_KEY = process.env.REACT_APP_IZIPAY_PUBLIC_KEY || "VErethUtraQuxas57wuMuquprADrAHAb";
const RENDER_MODE = process.env.REACT_APP_IZIPAY_RENDER_MODE || "popup";
const REDIRECT_URL =
  process.env.REACT_APP_IZIPAY_REDIRECT_URL ||
  "https://server.punto-web.com/comercio/creceivedemo.asp?p=h1";
const LOGO =
  process.env.REACT_APP_IZIPAY_APPEARANCE_LOGO ||
  "https://demo-izipay.azureedge.net/test/img/millasb.svg";
const IPN_URL =
  process.env.REACT_APP_IZIPAY_IPN_URL ||
  `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/payments/izipay/webhook`;
const RECETA_CURRENCY = "PEN";

const MOCK_RECETAS = [
  {
    id: 1,
    medicamento: "Losartán 50mg",
    indicacion: "1 comprimido cada 12 horas con alimentos.",
    doctor: "Dr. Roberto Vega",
    specialty: "Cardiología",
    emitida: "2026-02-28",
    vencimiento: "2026-05-28",
    status: "Activa",
  },
  {
    id: 2,
    medicamento: "Hidrocortisona crema 1%",
    indicacion: "Aplicar 2 veces al día en zona afectada por 2 semanas.",
    doctor: "Dra. Patricia Lima",
    specialty: "Dermatología",
    emitida: "2026-02-16",
    vencimiento: "2026-03-02",
    status: "Activa",
  },
  {
    id: 3,
    medicamento: "Ibuprofeno 400mg",
    indicacion: "1 comprimido cada 8 horas en caso de dolor. Máximo 5 días.",
    doctor: "Dr. Marco Castro",
    specialty: "Medicina General",
    emitida: "2026-01-30",
    vencimiento: "2026-02-04",
    status: "Vencida",
  },
  {
    id: 4,
    medicamento: "Amoxicilina 500mg",
    indicacion: "1 cápsula cada 8 horas por 7 días.",
    doctor: "Dr. Marco Castro",
    specialty: "Medicina General",
    emitida: "2025-12-01",
    vencimiento: "2025-12-08",
    status: "Vencida",
  },
];

export default function MisRecetas() {
  const user = useUserData(axiosInstance) || MOCK_USER;
  const [filter, setFilter] = React.useState("Todas");

  // ── Precio receta desde el admin ──────────────────────────────
  const [recetaAmount, setRecetaAmount] = React.useState("5.00");
  React.useEffect(() => {
    axiosInstance
      .get("/api/settings")
      .then((r) => {
        const row = (r.data.data || []).find((s) => s.key === "receta_precio_pen");
        if (row) setRecetaAmount(Number(row.value).toFixed(2));
      })
      .catch(() => {}); // fallback al default "5.00"
  }, []);

  // ── Estado del modal de nueva receta ─────────────────────────
  const [showModal, setShowModal] = React.useState(false);
  const [payState, setPayState] = React.useState("idle"); // idle | loading_token | ready | paying | done | error
  const [izipayToken, setIzipayToken] = React.useState(null);
  const [txId, setTxId] = React.useState(null);
  const [orderNum, setOrderNum] = React.useState(null);

  const currentUser = React.useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return {};
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return {};
    }
  }, []);

  const handleOpenModal = () => {
    setShowModal(true);
    setPayState("loading_token");

    const { transactionId, orderNumber } = getDataOrderDynamic();
    setTxId(transactionId);
    setOrderNum(orderNumber);

    GetTokenSession(transactionId, {
      merchantCode: MERCHANT_CODE,
      publicKey: PUBLIC_KEY,
      orderNumber,
      amount: recetaAmount,
      currency: RECETA_CURRENCY,
    })
      .then((resp) => {
        if (resp?.response?.token) {
          setIzipayToken(resp.response.token);
          setPayState("ready");
        } else {
          console.error("[MisRecetas] Sin token:", resp);
          setPayState("error");
        }
      })
      .catch((err) => {
        console.error("[MisRecetas] Error token:", err);
        setPayState("error");
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPayState("idle");
    setIzipayToken(null);
  };

  const handlePay = async () => {
    if (!izipayToken) return;
    setPayState("paying");
    try {
      await loadIzipaySdk();

      const izi = new window.Izipay({
        publicKey: PUBLIC_KEY,
        config: {
          transactionId: txId,
          action: "pay",
          merchantCode: MERCHANT_CODE,
          order: {
            orderNumber: orderNum,
            currency: RECETA_CURRENCY,
            amount: recetaAmount,
            processType: "AT",
            merchantBuyerId: currentUser.id || "vitalclic-user",
            dateTimeTransaction: Number(`${Date.now()}000`),
            payMethod: "CARD,QR,YAPE_CODE,PAGO_PUSH",
          },
          card: { brand: "", pan: "" },
          billing: {
            firstName: currentUser.name || "Usuario",
            lastName: currentUser.lastName || "Vitalclic",
            email: currentUser.email || "contacto@vitalclic.com",
            phoneNumber: currentUser.phone || "999999999",
            street: currentUser.address || "Av. Salud 123",
            city: currentUser.province || "Lima",
            state: currentUser.department || "Lima",
            country: "PE",
            postalCode: currentUser.postalCode || "15001",
            documentType: "DNI",
            document: currentUser.dni || "12345678",
          },
          render: {
            typeForm: RENDER_MODE === "embed" ? "in-frame" : "pop-up",
            container: "#izipay-receta-container",
          },
          urlRedirect: REDIRECT_URL,
          appearance: { logo: LOGO },
          urlIPN: IPN_URL,
        },
      });

      izi.LoadForm({
        authorization: izipayToken,
        keyRSA: "RSA",
        callbackResponse: (response) => {
          console.log("[MisRecetas] callbackResponse:", response);
          if (String(response?.code) === "00") {
            setPayState("done");
            alert("✅ ¡Receta solicitada exitosamente! Te notificaremos cuando esté lista.");
            handleCloseModal();
          } else {
            setPayState("ready");
            alert(`⚠️ Problema con el pago (Código: ${response?.code}). Intentá nuevamente.`);
          }
        },
      });
    } catch (err) {
      console.error("[MisRecetas] Error al abrir checkout:", err);
      setPayState("error");
    }
  };

  const filtered =
    filter === "Todas" ? MOCK_RECETAS : MOCK_RECETAS.filter((r) => r.status === filter);

  const activas = MOCK_RECETAS.filter((r) => r.status === "Activa").length;
  const vencidas = MOCK_RECETAS.filter((r) => r.status === "Vencida").length;

  return (
    <div className={layoutStyles.panelPage}>
      <PanelSidebar user={user} />

      <main className={layoutStyles.main}>
        <PanelHeader label="Mi panel" title="Mis recetas" user={user} />
        <p className={styles.subtitle}>Medicamentos activos y recetas anteriores.</p>

        {/* Stats */}
        <div className={styles.statsRow}>
          <div className={layoutStyles.statCard}>
            <p className={layoutStyles.statValue}>{MOCK_RECETAS.length}</p>
            <p className={layoutStyles.statTitle}>Total recetas</p>
          </div>
          <div className={layoutStyles.statCard}>
            <p className={`${layoutStyles.statValue} ${layoutStyles.teal}`}>{activas}</p>
            <p className={layoutStyles.statTitle}>Activas</p>
          </div>
          <div className={layoutStyles.statCard}>
            <p className={layoutStyles.statValue}>{vencidas}</p>
            <p className={layoutStyles.statTitle}>Vencidas</p>
          </div>
        </div>

        {/* Filtro */}
        <div className={styles.toolbar}>
          {["Todas", "Activa", "Vencida"].map((f) => (
            <button
              key={f}
              className={`${styles.tab} ${filter === f ? styles.tabActive : ""}`}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
          <button className={styles.btnNuevaReceta} onClick={handleOpenModal}>
            + NUEVA RECETA
          </button>
        </div>

        {/* Lista */}
        <div className={layoutStyles.card}>
          {filtered.length === 0 ? (
            <p className={layoutStyles.emptyState}>No hay recetas en esta categoría.</p>
          ) : (
            filtered.map((r) => (
              <div key={r.id} className={styles.recetaRow}>
                <div className={styles.recetaLeft}>
                  <div className={styles.pillIcon}>💊</div>
                  <div>
                    <p className={styles.recetaMed}>{r.medicamento}</p>
                    <p className={styles.recetaInd}>{r.indicacion}</p>
                    <p className={styles.recetaMeta}>
                      {r.doctor} · {r.specialty}
                    </p>
                  </div>
                </div>
                <div className={styles.recetaRight}>
                  <span
                    className={
                      r.status === "Activa" ? layoutStyles.badgeActiva : layoutStyles.badgeVencida
                    }
                  >
                    {r.status}
                  </span>
                  <p className={styles.recetaFecha}>Emitida: {formatDate(r.emitida)}</p>
                  <p className={styles.recetaFecha}>Vence: {formatDate(r.vencimiento)}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* ── Modal solicitar receta ───────────────────────── */}
      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox}>
            <button className={styles.modalClose} onClick={handleCloseModal} aria-label="Cerrar">
              ×
            </button>
            <div className={styles.modalIcon}>📋</div>
            <h3 className={styles.modalTitle}>Solicitar receta digital</h3>
            <p className={styles.modalDesc}>
              Completá el pago para que un profesional emita tu receta de forma digital.
            </p>
            <div className={styles.modalAmount}>
              Total: <strong>{RECETA_CURRENCY} {recetaAmount}</strong>
            </div>

            <button
              className={styles.btnPagar}
              onClick={handlePay}
              disabled={
                payState === "loading_token" ||
                payState === "paying" ||
                payState === "error"
              }
            >
              {payState === "loading_token" && "Preparando pasarela segura..."}
              {payState === "ready" && "Pagar con IziPay"}
              {payState === "paying" && "Abriendo ventana de pago..."}
              {payState === "error" && "Error de conexión — intentá de nuevo"}
            </button>

            <div id="izipay-receta-container" />

            {window.location.hostname === "localhost" && (
              <button
                className={styles.btnSimular}
                onClick={() => {
                  setPayState("done");
                  alert("✅ Pago SIMULADO (dev). Receta solicitada.");
                  handleCloseModal();
                }}
              >
                🧪 Simular pago (Dev only)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
